// ── /company/compare — the registry ─────────────────────────────────────────
// Client-safe. The metadata for every comparison innernet publishes: who the
// other product is, what shelf it sits on, and how its icon is drawn. The prose
// lives next door in content/compare/<slug>.md and is read server-side by
// lib/compare (fs) — this file carries nothing that needs a filesystem, so the
// thumbnail and the index client can import it freely.
//
// Icons are the other product's OWN app icon (nominative use on a comparison
// page), fetched from their site and kept at public/brand/compare/<key>.png at
// 256px (egoist's is the apple-touch-icon at ego.ist, 2026-10-15). Two are
// drawn rather than fetched: mymind (only a 64px favicon exists) and rewind
// (the app is gone and the domain now belongs to someone else, so the mark is
// a remembrance). innernet's tile is the AppIcon mark.

import type { Tone } from '../company/theme'

export type CompareCategory = 'portable memory' | 'agent memory' | 'personal ai' | 'notes & knowledge' | 'assistants'

/** the lenses on the index, in ticker order. `all` is implicit. */
export const COMPARE_CATEGORIES: { key: CompareCategory; label: string; dek: string }[] = [
  {
    key: 'portable memory',
    label: 'portable memory',
    dek: 'memory that belongs to the person and goes with them to every ai app they use. the shelf innernet sits on.',
  },
  {
    key: 'agent memory',
    label: 'agent memory',
    dek: 'memory infrastructure a developer installs so an agent stops forgetting — the layer under someone else’s product.',
  },
  {
    key: 'personal ai',
    label: 'personal ai',
    dek: 'products that build a copy, a companion or a private client around you — memory that lives inside one app.',
  },
  {
    key: 'notes & knowledge',
    label: 'notes & knowledge',
    dek: 'the things you save, write and hear — libraries, second brains, meeting notes and study tools.',
  },
  {
    key: 'assistants',
    label: 'assistants',
    dek: 'the memory built into chatgpt and claude — what it holds, what it cannot, and where a layer fits.',
  },
]

/** one product innernet is compared against, as it appears on a tile. */
export interface CompareSide {
  /** what the tile is called under the icon */
  name: string
  /** BrandIcon registry key (innernet · openai · claude), OR a file on the compare shelf */
  icon: { brand: string } | { src: string }
}

export interface ComparisonMeta {
  /** the page: /company/compare/<slug> — or, for a piece that lives on the blog, its own href */
  slug: string
  href: string
  /** the two tiles, left then right, exactly as the title reads them */
  left: CompareSide
  right: CompareSide
  competitor: string
  site: string | null
  category: CompareCategory
  title: string
  dek: string
  /** ISO date the comparison was last checked against the other product's site */
  updated: string
  /**
   * The other product's identity colour — the page's organism and, at the house
   * interval, its ground (lib/company/theme). Measured off their own icon;
   * `chosen` marks the four monochrome marks that had no hue to measure.
   */
  tone: Tone
  /** true when the prose is a company post rather than a content/compare file */
  onBlog?: boolean
}

export const INNERNET_SIDE: CompareSide = { name: 'innernet', icon: { brand: 'innernet' } }

const shelf = (name: string, key: string): CompareSide => ({ name, icon: { src: `/brand/compare/${key}.png` } })

const vs = (
  key: string,
  competitor: string,
  site: string,
  category: CompareCategory,
  tone: Tone,
  dek: string,
  updated = '2026-09-03',
): ComparisonMeta => {
  const slug = `${competitor.replace(/\./g, '-')}-vs-innernet`
  return {
    slug,
    href: `/company/compare/${slug}`,
    left: shelf(competitor, key),
    right: INNERNET_SIDE,
    competitor,
    site,
    category,
    title: `${competitor} vs innernet`,
    dek,
    updated,
    tone,
  }
}

/**
 * Every comparison, in the order the index shows them. The deks here are the
 * card lines; the .md frontmatter carries the same text and the test keeps the
 * two from drifting.
 */
export const COMPARISONS: ComparisonMeta[] = [
  // ── the assistants' own memory ──
  {
    slug: 'chatgpt-memory-vs-claude-memory',
    href: '/company/chatgpt-memory-vs-claude-memory',
    left: { name: 'chatgpt', icon: { brand: 'openai' } },
    right: { name: 'claude', icon: { brand: 'claude' } },
    competitor: 'chatgpt · claude',
    site: null,
    category: 'assistants',
    title: 'ChatGPT memory vs Claude memory: neither can commit what the innernet can',
    dek: 'both assistants rebuilt their memory in 2026. neither one remembers your work — and why what’s missing isn’t a smarter assistant, but a layer.',
    updated: '2026-09-07',
    // the piece lives on the blog and wears the blog's room; the tone is here
    // so every entry answers the same questions.
    tone: { hue: 25, sat: 0.62, chosen: true },
    onBlog: true,
  },

  // ── portable memory ──
  // the closest in words (read 2026-10-15): a consent broker over MCP. tone is
  // chosen, the mark is white on black.
  vs('egoist', 'egoist', 'https://ego.ist', 'portable memory', { hue: 262, sat: 0.5, chosen: true }, "egoist guards the door to your memory. innernet keeps what's behind the door current.", '2026-10-15'),

  // ── agent memory ──
  vs('mem0', 'mem0', 'https://mem0.ai', 'agent memory', { hue: 260, sat: 0.72 }, 'mem0 is memory for your app. innernet is memory for you.'),
  vs('supermemory', 'supermemory', 'https://supermemory.ai', 'agent memory', { hue: 206, sat: 0.6, chosen: true }, 'supermemory is racing to make retrieval fast and accurate. innernet is trying to make the conclusion right.'),
  vs('plur', 'plur', 'https://plur.ai', 'agent memory', { hue: 188, sat: 0.77 }, 'plur remembers what your agents should do. innernet remembers who you are.'),
  vs('evermind', 'evermind', 'https://evermind.ai', 'agent memory', { hue: 42, sat: 0.82 }, "evermind's memory belongs to the agent. innernet's belongs to the person, and the agent gets read access."),
  vs('memorylake', 'memorylake', 'https://memorylake.ai', 'agent memory', { hue: 196, sat: 0.55, chosen: true }, 'memorylake built a lake. innernet is building a life.'),

  // ── personal ai ──
  vs('anuma', 'anuma', 'https://anuma.ai', 'personal ai', { hue: 27, sat: 0.82 }, 'anuma is the closest thing to us in spirit, and the furthest in architecture.'),
  vs('personalai', 'personal.ai', 'https://personal.ai', 'personal ai', { hue: 228, sat: 0.55, chosen: true }, 'personal.ai wants to build a copy of you. innernet wants every AI you already use to know you.'),
  vs('mymemory', 'mymemory', 'https://mymemory.ai', 'personal ai', { hue: 1, sat: 0.72 }, 'mymemory is a keepsake. innernet is a working memory.'),
  vs('rewind', 'rewind', 'https://rewind.ai', 'personal ai', { hue: 228, sat: 0.8 }, "rewind was the best consumer memory product anyone had built. it isn't a competitor any more. it's the argument."),

  // ── notes & knowledge ──
  vs('mymind', 'mymind', 'https://mymind.com', 'notes & knowledge', { hue: 17, sat: 0.82 }, 'mymind gives you back what you put in. innernet gives back what was made of it.'),
  vs('reflect', 'reflect', 'https://reflect.app', 'notes & knowledge', { hue: 253, sat: 0.62 }, 'in reflect, you draw the graph. in innernet, the graph gets drawn from your work.'),
  vs('mem', 'mem', 'https://mem.ai', 'notes & knowledge', { hue: 7, sat: 0.8 }, "mem organises your team's work. innernet understands your thinking."),
  vs('granola', 'granola', 'https://granola.ai', 'notes & knowledge', { hue: 68, sat: 0.55 }, 'granola remembers the meeting. innernet remembers the project the meeting was about.'),
  vs('recall', 'recall', 'https://getrecall.ai', 'notes & knowledge', { hue: 250, sat: 0.55, chosen: true }, 'recall remembers what you consumed. innernet remembers what you decided.'),
  vs('memo', 'memo', 'https://memo.cards', 'notes & knowledge', { hue: 37, sat: 0.8 }, 'memo helps you remember. innernet makes your tools remember.'),
]

/** the comparisons that render from content/compare (everything not on the blog). */
export const FILE_COMPARISONS = COMPARISONS.filter((c) => !c.onBlog)

export function comparisonMeta(slug: string): ComparisonMeta | null {
  return COMPARISONS.find((c) => c.slug === slug) ?? null
}

/** the newest `updated` across the set — the index's own freshness date. */
export const COMPARE_UPDATED = COMPARISONS.map((c) => c.updated).sort().at(-1) ?? '2026-09-03'

// The index's own words — the one page on the site that answers "which AI
// memory tool" head-on, so it is written plainly (the FAQ voice), and the same
// strings feed the FAQPage schema so the answer engines read what people read.
export const COMPARE_TITLE = 'innernet vs the memory category'
export const COMPARE_DEK =
  'honest comparisons of innernet against the ai memory tools people ask us about — what kind of memory each one is building, what innernet is building, and why the difference matters.'
export const COMPARE_DESCRIPTION =
  'innernet compared with egoist (ai passport), mem0, supermemory, plur, evermind, memorylake, anuma, personal.ai, mymemory, rewind, mymind, reflect, mem, granola, recall and memo — plus chatgpt memory vs claude memory. what each ai memory tool remembers, who owns it, and which one you want.'

export const COMPARE_FAQS: { q: string; a: string }[] = [
  {
    q: 'what is an ai memory layer?',
    a: 'a memory layer is a place your context lives outside any single ai tool, which every tool you connect can read and write. innernet is one: it holds your projects, decisions and reasoning as versioned documents, keeps them current with an agent called netti, and serves them over mcp so claude, chatgpt, cursor, codex and any other client see the same memory.',
  },
  {
    q: 'how is innernet different from mem0 or supermemory?',
    a: 'mem0 and supermemory are memory infrastructure a developer installs into their own product, so the memory belongs to that application. innernet is memory for the person: it is keyed to you, lives outside every tool, and each tool you use is a guest with read access. they are not really substitutes; a product built on mem0 could sit above innernet.',
  },
  {
    q: 'does innernet replace the memory built into chatgpt or claude?',
    a: 'no. the built-in memory models who you are, and it does that well. innernet holds what the work is: decisions with a commit history, contradictions kept as branches, reads you can address by name. it connects to both as an mcp server and works alongside their own memory.',
  },
  {
    q: 'which ai memory tool should i use?',
    a: 'if your problem is an agent that forgets its users, use an infrastructure product like mem0 or supermemory. if you want a private place for the things you find, use mymind or reflect. if your days are meetings, use granola. if your work moves between several ai tools and you are tired of re-explaining it to each one, that is what innernet is for. most of these pair with innernet rather than compete with it.',
  },
  {
    q: 'what happens to my memory if innernet shuts down?',
    a: 'you can export everything and delete everything on demand, deletion is immediate with backups purged within 30 days, and the memory is reachable over an open mcp endpoint by tools innernet does not control. those three properties are the only version of the promise that survives the company, which is why the rewind comparison is on this page.',
  },
]
